import React, {useState} from 'react';

import PropTypes from 'prop-types';
import classNames from 'classnames';
import {useDispatch, useSelector} from 'react-redux';

import OutputText from '@ey/canvascoreservices/OutputText';
import Utility from '../../../../../../util/utilityfunctions';
import {currentResource, labels} from '../../../../../../util/utils';
import {projectSessionKeys, routes} from '../../../../../../util/uiconstants';

import {addContactsTabs} from '../../../../../../util/uiconstants';
import {
	ACCESS_TO_CONTACT_LIST,
	ACCESS_TO_DASHBOARD,
	DASHBOARD_VIEWS,
	ENTITIES,
	HAS_EXTERNAL,
	SELECTED_USER,
	USER_LIST,
	CUSTOM_COLUMNS,
	TAGS,
	ACCESS_TO_GROUP_RISK_ASSESMENT,
	PROJECT_OWNER_ACCESS,
	ENTITY_ADMIN_ACCESS,
	PROJECT_OWNER,
	ENTITY_ADMIN,
	USER
} from './utils/constants';

import normalizeAddContactData from './utils/normalizeAddContactData';
import {getDashBoardViewsValues} from './utils/getDashBoardViewsValues';
import {raiseJSValidationError} from '../../../../../../actions/erroractions';

import {addContactToEntityProject} from '../../../../../../actions/ContactDB';
import {getProjectDetails} from '../../../../../../actions/Project/projectAction';
import {getOrgChart} from '../../../../../../actions/Project/orgStructureAction';

import {MotifNavigationIcClose24px} from '@ey-xd/motif-icon';

import TabNavigation from '../../../../../CoreWrappers/TabNavigation';
import useForm from '../../../../../CoreWrappers/Form/useForm';
import Button from '../../../../../CoreWrappers/Button';
import Modal from '../../../../../CoreWrappers/Modal';
import User from '../../../../../CoreWrappers/User';
import AccessDefinePanel from './AccessDefinePanel';
import AttributesPanel from './AttributesPanel';
import TagsPanel from './TagsPanel';
import StyledGrid from './StyledGrid';
import ContactSelector from './ContactSelector';
import getUserTypeValues from './utils/getUserTypeValues';
import BorderBox from '../../../../../Common/BorderBox';
import Hr from '../../../../../Common/Hr/HrAlt';
import StyledContactList from './StyledContactList';
import ListWithScroll from '../../../../../Common/ListWithScroll';

const ACForm = ({showModal, hideModal}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [tab, setTab] = useState(currentResource.addContactsTabsResource[0]);
	const projectId = Utility.getProjectIdFromUrl();
	const entities = useSelector((state) => state.contacts.entities);
	const project = useSelector((store) => store.project);
	const engagementMetadata = useSelector((store) => store?.engagementMetaData);
	const orgChart = useSelector((store) => store.dashboardContext?.orgChart);

	const userPreferences = useSelector((store) => store.userPreferences);
	const userType = userPreferences?.userType;
	const userGui = userPreferences?.gui;

	const isProjectOwner = project.users?.find(({userId}) => userId === userGui)?.isProjectOwner;
	const isGroupRiskAssessment = project.isGroupRiskAssessmentProject;

	const projectSessionKey = `${projectSessionKeys.projectdetail}_${projectId}`;

	const dispatch = useDispatch();

	const handleGetOrgChart = () =>
		dispatch(
			getOrgChart(projectId, {
				projectId: projectId,
				projectSessionKey: projectSessionKey,
				loadFromSession: true,
				isContactsPage: true,
				isGroupRiskAssessment,
				userType,
				engagementMetadata,
				isRiskAssessmentPage: false,
				isProjectOwner,
				userId: userGui
			})
		).then(() => {
			dispatch(getProjectDetails(projectId));
		});

	const getAttributesObject = () => {
		const customColumns = project?.customColumns?.map((column) => ({
			name: column.name,
			id: column.id,
			columnValues: column.options.map(({option, id}) => ({name: option, id})),
			value: null
		}));
		return Utility.sortListData(customColumns, 'name');
	};

	const dispatchDuplicatedContactToast = (repeatedUserName) => {
		let repeatedUserMessage = `${repeatedUserName} ${labels.duplicatedUser}`;
		dispatch(raiseJSValidationError(repeatedUserMessage));
	};

	const dispatchNoContactsSelectedToast = () => {
		dispatch(raiseJSValidationError(labels.mustSelectOne));
	};

	const dispatchNoEntitySelectedToast = () => {
		dispatch(raiseJSValidationError(labels.mustSelectOneEntity));
	};

	const dispatchNoViewsSelectedToast = () => {
		dispatch(raiseJSValidationError(labels.mustSelectOneView));
	};

	const dispatchExternalUserDomainNotAllowed = (entities) => {
		dispatch(raiseJSValidationError(`${labels.externalUserDomainMismatch}<br>${labels.entitiesLabels}:${entities}`));
	};

	const isUserAlreadyAdded = (selectedUser, userList) => {
		if (selectedUser.emailAddress) {
			const flatContact = entities.flatMap((entity) => entity.contacts);
			const allContacts = [...flatContact, ...userList];
			return allContacts.some((user) => user.emailAddress.toLowerCase() === selectedUser.emailAddress.toLowerCase());
		}
		return false;
	};

	const renderInstructionText = () => (
		<OutputText light className="full-row">
			{labels.addContactInstructional}
		</OutputText>
	);

	const renderTabs = () => {
		const props = {
			tabs: currentResource.addContactsTabsResource,
			activeTab: tab,
			setTab: setTab
		};

		return (
			<>
				<TabNavigation {...props} />
			</>
		);
	};

	const {dashboardViews} = getDashBoardViewsValues(orgChart, entities, isProjectOwner, userGui);

	const initialValues = {
		[SELECTED_USER]: null,
		[USER_LIST]: [],
		[HAS_EXTERNAL]: false,
		[PROJECT_OWNER_ACCESS]: false,
		[ENTITY_ADMIN_ACCESS]: false,
		[ENTITIES]: null,
		[ACCESS_TO_GROUP_RISK_ASSESMENT]: false,
		[DASHBOARD_VIEWS]: dashboardViews,
		[ACCESS_TO_CONTACT_LIST]: false,
		[CUSTOM_COLUMNS]: getAttributesObject(),
		[TAGS]: null
	};

	const schema = {
		[SELECTED_USER]: {required: false},
		[USER_LIST]: {required: false},
		[HAS_EXTERNAL]: {required: false},
		[PROJECT_OWNER_ACCESS]: {required: false},
		[ENTITY_ADMIN_ACCESS]: {requiered: false},
		[ENTITIES]: {requiered: false},
		[ACCESS_TO_GROUP_RISK_ASSESMENT]: {required: false},
		[DASHBOARD_VIEWS]: {required: false},
		[ACCESS_TO_CONTACT_LIST]: {required: false},
		[CUSTOM_COLUMNS]: {required: false},
		[TAGS]: {required: false}
	};

	const {values, errors, handleChange, handleSubmit, handleReset, isFormValid} = useForm({
		initialValues,
		schema,
		onSubmit: (values, callback) => {
			setIsLoading(true);
			const userListSelected = values[USER_LIST];
			const contactListPermissions = values[DASHBOARD_VIEWS];
			const entitiesList = values[ENTITIES];
			const toggleContactList = values[ACCESS_TO_CONTACT_LIST];
			const tagsSelectedList = values[TAGS];
			const customColumnsList = values[CUSTOM_COLUMNS];
			const toggleGroupRiskAssessmentAccess = values[ACCESS_TO_GROUP_RISK_ASSESMENT];
			let valid = true;
			let userLevelAccess = USER;

			if (values[PROJECT_OWNER_ACCESS]) userLevelAccess = PROJECT_OWNER;
			if (values[ENTITY_ADMIN_ACCESS]) userLevelAccess = ENTITY_ADMIN;

			if (userListSelected?.length === 0 && valid) {
				dispatchNoContactsSelectedToast();
				setIsLoading(false);
				valid = false;
			}

			if (!(entitiesList?.length > 0) && valid) {
				dispatchNoEntitySelectedToast();
				setIsLoading(false);
				valid = false;
			}

			let isChecked = contactListPermissions.some((x) => x.checked === true);
			if (!isChecked && values[ACCESS_TO_DASHBOARD] && valid) {
				dispatchNoViewsSelectedToast();
				setIsLoading(false);
				valid = false;
			}

			let entitiesWithValidDomain = [];
			let entitiesWithInvalidDomain = {ids: [], labels: []};

			userListSelected.forEach((us) => {
				if (!us.isInternal) {
					const userDomain = `@${us.emailAddress.split('@')[1]}`;

					entitiesList.forEach((ent) => {
						let isUserDomainInEntity = false;
						let entityDomains = project.orgStructure.find((en) => en.entityId === ent.entityId).domains;

						if (entityDomains?.length > 0) {
							entityDomains.forEach((d) => {
								let domainPattern = d.replace('@', '\\@');
								domainPattern = domainPattern.replaceAll('*', '\\w+');
								domainPattern = domainPattern.replaceAll('.', '\\.*');

								let regexPattern = new RegExp(domainPattern);

								if (regexPattern.test(userDomain)) {
									isUserDomainInEntity = true;
									entitiesWithValidDomain.push(ent);
								}
							});
						}

						if (!isUserDomainInEntity) {
							if (!entitiesWithInvalidDomain.ids.includes(ent.entityId)) {
								entitiesWithInvalidDomain.ids.push(ent.entityId);
								entitiesWithInvalidDomain.labels.push(` ${ent.label}`);
							}
						}
					});

					if (entitiesWithInvalidDomain.ids.length > 0) {
						dispatchExternalUserDomainNotAllowed(entitiesWithInvalidDomain.labels);
						valid = false;
						setIsLoading(false);
					}
				}
			});

			if (valid) {
				let body = normalizeAddContactData(
					contactListPermissions,
					toggleContactList,
					userListSelected,
					tagsSelectedList,
					customColumnsList,
					toggleGroupRiskAssessmentAccess,
					userLevelAccess
				);
				const apiCalls = [];

				if (values[PROJECT_OWNER_ACCESS]) {
					const entity = entitiesList[0];
					apiCalls.push(() => dispatch(addContactToEntityProject(projectId, entity.entityId, body)));
				} else {
					entitiesList.forEach((entity) => {
						apiCalls.push(() => dispatch(addContactToEntityProject(projectId, entity.entityId, body)));
					});
				}

				const sequentionallyDispatchApiArray = async () => {
					// This array of API calls needs to be executed sequentionally otherwise it fails (cos' CosmoDB)
					const results = [];
					for (const call of apiCalls) results.push(await Promise.allSettled([call()]));
					return results.flat();
				};
				sequentionallyDispatchApiArray()
					.then(() => {
						callback();
						setIsLoading(false);
					})
					.catch(() => {
						setIsLoading(false);
					});
			}
		}
	});

	const renderAddContact = () => {
		const id = SELECTED_USER;

		const handleOnAdd = (value) => {
			if (isUserAlreadyAdded(value, values[USER_LIST])) {
				const displayName = `${value.name} ${value.lastName}`;
				dispatchDuplicatedContactToast(displayName);
			} else {
				const newValue = [...values[USER_LIST], value];
				const newValues = {[USER_LIST]: newValue, ...getUserTypeValues(values, newValue)};
				handleChange(id, null, newValues);
			}
		};

		const handleOnChange = (value) => {
			handleChange(id, value);
		};

		const props = {
			id,
			value: values[id],
			onChange: handleOnChange,
			onAdd: (value) => handleOnAdd(value),
			onAddDisabled: values[id] === null,
			projectId: projectId
		};

		return <ContactSelector {...props} />;
	};

	const renderUserList = () => {
		const id = USER_LIST;

		const removeContact = (idUser) => {
			const newValue = values[id].filter((user) => user.userId !== idUser);
			const newValues = {[USER_LIST]: newValue, ...getUserTypeValues(values, newValue)};
			handleChange(SELECTED_USER, null, newValues);
		};

		const renderRemove = (userId) => {
			const props = {
				onClick: () => removeContact(userId),
				type: 'button',
				svg: MotifNavigationIcClose24px,
				className: 'remove-user',
				title: labels.removeLabel,
				variant: 'ghost',
				iconSize: 20
			};

			return <Button {...props} />;
		};

		const renderItem = ({userId, name: firstName, lastName, emailAddress: email, isInternal}, index) => {
			const props = {
				key: userId,
				userId,
				firstName,
				lastName,
				email,
				isInternal,
				showInfo: false,
				showType: true
			};

			const isLast = index === values[id].length - 1;

			return (
				<>
					<div className="user">
						<User {...props} />
						{renderRemove(userId)}
					</div>
					{!isLast && <Hr />}
				</>
			);
		};

		const props = {
			list: values[id],
			item: renderItem,
			emptyTitle: labels.noContactsAdded,
			enableScroll: false
		};

		return (
			<StyledContactList className="contact-list">
				<ListWithScroll {...props} />
			</StyledContactList>
		);
	};

	const sharedProps = {
		values,
		errors,
		handleChange,
		handleSubmit,
		handleReset,
		isFormValid,
		isLoading,
		setIsLoading
	};

	const modalProps = {
		showButtons: true,
		show: showModal,
		title: labels.addContactTitle,
		closeTitle: labels.closeTitleModal,
		closeBtnTitle: labels.cancelLabel,
		confirmBtnTitle: labels.save,
		tootipPosition: 'top',
		hideConfirmButton: false,
		modalsize: 'medium',
		isOkButtonDisabled: isLoading,
		isCancelButtonDisabled: isLoading,
		modalbodystyle: 'no-padding',
		onHide: () => {
			handleReset();
			hideModal();
		},
		onOkClick: () => {
			handleSubmit(() => {
				handleGetOrgChart();
				handleReset();
				hideModal();
			});
		},
		isLoading
	};

	const isEmpty = tab.id === addContactsTabs.attributes && !values[CUSTOM_COLUMNS]?.length;
	const borderProps = {
		className: classNames({empty: isEmpty}),
		empty: isEmpty
	};

	return (
		<Modal {...modalProps}>
			{showModal && (
				<StyledGrid>
					{renderInstructionText()}
					<Hr className="full-row" />
					{renderAddContact()}
					{renderTabs()}
					{renderUserList()}
					<BorderBox {...borderProps}>
						{tab.id === addContactsTabs.access && <AccessDefinePanel {...sharedProps} />}
						{tab.id === addContactsTabs.attributes && <AttributesPanel {...sharedProps} />}
						{tab.id === addContactsTabs.tags && <TagsPanel {...sharedProps} />}
					</BorderBox>
				</StyledGrid>
			)}
		</Modal>
	);
};

ACForm.propTypes = {
	show: PropTypes.bool.isRequired,
	hideModal: PropTypes.func.isRequired
};

ACForm.defaultProps = {};

export default ACForm;
